import { InputRow, SelectRow, SwitchRow, DetailRow } from "../Rows";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { useAuth } from "../../providers/AuthContext";
import { getAllCountries, getAllOrganizations } from "../../api/Resources";
import { useTimeZones } from "../../hooks/useTimeZones";
import toast from "react-hot-toast";
import { FormCard, FormGrid } from "../FormCard";
import MapLocationPickerModal from "../MapLocationPickerModal";


const SiteForm = ({
    chargingSite,
    setChargingSite,
    setHasChanges,
    handleInputChange,
    isEdit,
    invalidFields = [],   
}) => {
    const { t } = useTranslation();

    const zonesFromHook = useTimeZones(true);
    const europeZonesLocal = zonesFromHook.map(z => ({ label: z, value: z }));

    const { getAccessToken } = useAuth();
    const [countries, setCountries] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [isMapOpen, setIsMapOpen] = useState(false);

    const mapSearchAddress = [
        chargingSite.address,
        chargingSite.zipCode,
        chargingSite.city,
        chargingSite.countryCode,
    ]
        .filter((part) => String(part || "").trim())
        .join(", ");

    useEffect(() => {
        fetchCountries();
        if (isEdit) {
            fetchOrganizations();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    const fetchCountries = async () => {
        try {
            const token = await getAccessToken();
            const result = await getAllCountries(token);
            setCountries(result || []);
        } catch (err) {
            console.error("Failed fetching countries", err);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const token = await getAccessToken();
            const result = await getAllOrganizations(token);
            const org = result.map(o => ({
                value: o.organizationPublicId,
                label: o.organizationName
            }));
            setOrganizations(org || []);
        } catch (err) {
            toast.error(err?.message);
        }
    };

    const countryOptions = countries.map(c => ({
        value: c.alpha2CountryCode,
        label: c.countryName,
    }));

    const parkingTypeOptions = [
        { value: "ALONG_MOTORWAY", label: t("site.parkingType.ALONG_MOTORWAY") },
        { value: "PARKING_GARAGE", label: t("site.parkingType.PARKING_GARAGE") },
        { value: "PARKING_LOT", label: t("site.parkingType.PARKING_LOT") },
        { value: "ON_DRIVEWAY", label: t("site.parkingType.ON_DRIVEWAY") },
        { value: "ON_STREET", label: t("site.parkingType.ON_STREET") },
        { value: "UNDERGROUND_GARAGE", label: t("site.parkingType.UNDERGROUND_GARAGE") },
    ];

    const energyMixOptions = [
        { value: "ENERGY-001", label: t("site.energyMix.ENERGY-001") },
        { value: "ENERGY-002", label: t("site.energyMix.ENERGY-002") },
        { value: "ENERGY-003", label: t("site.energyMix.ENERGY-003") },
    ];


    return (
        <FormCard title={t("form.siteDetails")}>
            <FormGrid>

                {/* Name */}
                <InputRow
                    label={t("form.siteName")}
                    value={chargingSite.name}
                    onChange={(v) => {
                        setChargingSite({ ...chargingSite, name: v });
                        setHasChanges(true);
                    }}
                    invalid={invalidFields?.includes("name")}
                />

                {/* City */}
                <InputRow
                    label={t("common.city")}
                    value={chargingSite.city}
                    onChange={(v) => {
                        setChargingSite({ ...chargingSite, city: v });
                        setHasChanges(true);
                    }}
                    invalid={invalidFields?.includes("city")}
                />

                {/* Address */}
                <InputRow
                    label={t("common.address")}
                    value={chargingSite.address}
                    onChange={(v) => {
                        setChargingSite({ ...chargingSite, address: v });
                        setHasChanges(true);
                    }}
                    invalid={invalidFields?.includes("address")}
                />

                {/* Zip */}
                <InputRow
                    label={t("common.zipCode")}
                    value={chargingSite.zipCode}
                    isNumber
                    onChange={(v) => {
                        setChargingSite({ ...chargingSite, zipCode: v });
                        setHasChanges(true);
                    }}
                    invalid={invalidFields?.includes("zipCode")}
                />

                {/* Latitude */}
                <InputRow
                    label={t("common.latitude")}
                    value={chargingSite.latitude}
                    isNumber
                    onChange={(v) => {
                        setChargingSite({ ...chargingSite, latitude: v });
                        setHasChanges(true);
                    }}
                    maxLength={9}
                />

                {/* Longitude */}
                <InputRow
                    label={t("common.longitude")}
                    value={chargingSite.longitude}
                    isNumber
                    onChange={(v) => {
                        setChargingSite({ ...chargingSite, longitude: v });
                        setHasChanges(true);
                    }}
                    maxLength={9}
                    action={
                        <button
                            type="button"
                            className="btn btn-outline-primary shrink-0"
                            onClick={() => setIsMapOpen(true)}
                            title={t("site.pickLocation")}
                        >
                            <FontAwesomeIcon icon={faLocationDot} />
                            {t("common.map")}
                        </button>
                    }
                />

                {/* Country */}
                {isEdit ? (
                    <DetailRow
                        label={t("common.country")}
                        value={
                            countryOptions.find(c => c.value === chargingSite.countryCode)?.label ||
                            chargingSite.countryCode ||
                            "-"
                        }
                    />
                ) : (
                    <SelectRow
                        label={t("common.country")}
                        value={chargingSite.countryCode}
                        options={countryOptions}
                        required
                        invalid={invalidFields?.includes("countryCode")}
                        onChange={(v) => handleInputChange("countryCode", v)}
                    />
                )}

                <SelectRow
                    label={t("common.timeZone")}
                    value={chargingSite.timeZone}
                    options={europeZonesLocal}
                    invalid={invalidFields?.includes("timeZone")}
                    onChange={(v) => handleInputChange("timeZone", v)}
                />

                {isEdit && (
                    <>
                        <SelectRow
                            label={t("form.parkingType")}
                            value={chargingSite.parkingType}
                            options={parkingTypeOptions}
                            invalid={invalidFields?.includes("parkingType")}
                            onChange={(v) => {
                                setChargingSite({ ...chargingSite, parkingType: v });
                                setHasChanges(true);
                            }}
                        />

                        <SwitchRow
                            label={t("form.chargingWhenClosed")}
                            checked={chargingSite.chargingWhenClosed}
                            onChange={(v) => {
                                setChargingSite({ ...chargingSite, chargingWhenClosed: v });
                                setHasChanges(true);
                            }}
                        />

                        <SelectRow
                            label={t("form.energyMix")}
                            value={chargingSite.energyMix}
                            options={energyMixOptions}
                            invalid={invalidFields?.includes("energyMix")}
                            onChange={(v) => handleInputChange("energyMix", v)}
                        />

                        <SelectRow
                            label={t("entity.operator")}
                            value={chargingSite.operatorBusinessPublicId}
                            options={organizations}
                            invalid={invalidFields?.includes("operatorBusinessPublicId")}
                            onChange={(v) => handleInputChange("operatorBusinessPublicId", v)}
                        />

                        <SelectRow
                            label={t("form.subOperator")}
                            value={chargingSite.subOperatorBusinessPublicId}
                            options={organizations}
                            invalid={invalidFields?.includes("subOperatorBusinessPublicId")}
                            onChange={(v) => handleInputChange("subOperatorBusinessPublicId", v)}
                        />

                        <SelectRow
                            label={t("form.owner")}
                            value={chargingSite.ownerBusinessPublicId}
                            options={organizations}
                            invalid={invalidFields?.includes("ownerBusinessPublicId")}
                            onChange={(v) => handleInputChange("ownerBusinessPublicId", v)}
                        />

                        <InputRow
                            label={t("form.helpPhone")}
                            value={chargingSite.helpPhone}
                            isNumber
                            onChange={(v) => {
                                setChargingSite({ ...chargingSite, helpPhone: v });
                                setHasChanges(true);
                            }}
                        />

                        <SwitchRow
                            label={t("table.notificationsEnabled")}
                            checked={chargingSite.isNotificationEnabled}
                            onChange={(v) => {
                                setChargingSite({ ...chargingSite, isNotificationEnabled: v });
                                setHasChanges(true);
                            }}
                        />
                    </>
                )}
            </FormGrid>

            <MapLocationPickerModal
                isOpen={isMapOpen}
                onClose={() => setIsMapOpen(false)}
                latitude={chargingSite.latitude}
                longitude={chargingSite.longitude}
                address={mapSearchAddress}
                onConfirm={({ latitude, longitude }) => {
                    setChargingSite((prev) => ({ ...prev, latitude, longitude }));
                    setHasChanges(true);
                }}
            />
        </FormCard>
    );
};

export default SiteForm;
